/**
 * LoRA Wizard Types
 * Shared types and interfaces for the multi-step LoRA training wizard
 */

// Available base models for training
export type ModelId = "flux-1-dev" | "flux-1.1-pro" | "sdxl-1.0" | "sd3";

export interface ModelInfo {
  id: ModelId;
  name: string;
  vendor: string;
  fidelity: number; // 1-5
  speed: number; // 1-5
  vram: number; // 1-5
  license: string;
  isRecommended?: boolean;
  bullets: string[];
}

// Reference image with description
export interface ReferenceImage {
  id: string;
  file: File;
  previewUrl: string;
  description: string;
  hasTriggerWord: boolean;
  needsUpscale?: boolean; // true if image is below 1024x1024
}

// Training settings
export interface TrainingSettings {
  rank: number; // 4-64, default 16
  epochs: number; // 8-80, default 32
  learningRate: number; // 0.0001-0.002, default 0.0004
  snapshotEvery: 1 | 2 | 4 | 8 | 16 | "final";
  augmentation: AugmentationSettings;
}

export interface AugmentationSettings {
  horizontalFlip: boolean;
  cropJitter: boolean;
  colourJitter: boolean;
  noise: boolean;
}

// Epoch status during training
export type EpochStatusType = "pending" | "running" | "complete";

export interface EpochStatus {
  epoch: number;
  status: EpochStatusType;
  loss?: number;
}

// Epoch snapshot with 4 preview images
export interface EpochSnapshot {
  epoch: number;
  timestamp: Date;
  loss: number;
  previewImages: string[]; // mock mode: direct preview image URLs
  /**
   * Real mode (Phase 4): asset IDs of the checkpoint's in-loop sample
   * previews (`checkpoint.sampleInferenceAssetIds`). Resolved to signed
   * URLs at render time via useAssetUrl. Distinct from previewImages
   * (which are direct URLs used by the simulator).
   */
  sampleAssetIds?: string[];
}

// Training progress state
export interface TrainingProgress {
  currentEpoch: number;
  totalEpochs: number;
  timeRemaining: string;
  epochs: EpochStatus[];
  snapshots: EpochSnapshot[]; // Snapshots taken every N epochs
  snapshotInterval: number; // e.g., 8 = snapshot every 8 epochs
  startedAt?: Date;
}

// Test prompt result
export interface TestResult {
  id: string;
  promptNumber: number;
  prompt: string;
  imageUrl: string;
  score: number;
  accepted: boolean;
}

// Final training summary
export interface TrainingSummary {
  datasetSize: number;
  lightingConditions: number;
  triggerWord: string;
  baseModel: string;
  /** Friendly base-model name (e.g. "FLUX.1 Dev (LoRA)") resolved from the
   *  real catalog. Optional: mock/demo mode omits it and LockSlide falls back
   *  to the mock MODELS lookup, then to the raw `baseModel` slug. */
  baseModelLabel?: string;
  rank: number;
  epochs: number;
  finalLoss: number;
  likenessScore: number;
  trainedAt: Date;
  gpuMinutes: number;
  cost: number;
  artifactFilename: string;
  artifactSize: string;
}

// Main wizard state
export interface LoraWizardState {
  // Current slide (0-6)
  currentSlide: number;

  // Navigation lock (during training)
  isLocked: boolean;

  // Slide 1: Model selection
  selectedModel: ModelId | null;

  // Slide 2: References
  references: ReferenceImage[];

  // Slide 3: Settings
  settings: TrainingSettings;

  // Slide 4-5: Training
  isTraining: boolean;
  trainingProgress: TrainingProgress | null;

  // Slide 6: Testing
  testResults: TestResult[];

  // Slide 7: Final summary
  trainingSummary: TrainingSummary | null;

  // Wizard completion
  isComplete: boolean;
}

// Wizard action types
export interface LoraWizardActions {
  // Navigation
  nextSlide: () => void;
  prevSlide: () => void;
  goToSlide: (index: number) => void;

  // Slide 1
  selectModel: (modelId: ModelId) => void;

  // Slide 2
  addReferences: (files: File[]) => void;
  removeReference: (id: string) => void;
  updateReferenceDescription: (id: string, description: string) => void;
  toggleReferenceTriggerWord: (id: string) => void;

  // Slide 3
  updateSettings: (settings: Partial<TrainingSettings>) => void;
  updateAugmentation: (settings: Partial<AugmentationSettings>) => void;

  // Slide 4-5: Training
  startTraining: () => void;
  cancelTraining: () => void;

  // Slide 6: Testing
  regenerateTest: (testId: string) => void;
  acceptTest: (testId: string) => void;

  // Slide 7
  setTriggerWord: (word: string) => void;
  lockLora: () => void;
  saveAsDraft: () => void;

  // Reset
  resetWizard: () => void;
}

// Combined store type
export type LoraWizardStore = LoraWizardState & LoraWizardActions;

// Slide configuration
export interface SlideConfig {
  id: number;
  name: string;
  canAdvance: (state: LoraWizardState) => boolean;
  canGoBack: (state: LoraWizardState) => boolean;
}

// Model data constant
export const MODELS: ModelInfo[] = [
  {
    id: "flux-1-dev",
    name: "FLUX 1 Dev",
    vendor: "Black Forest Labs",
    fidelity: 4,
    speed: 3,
    vram: 3,
    license: "Non-commercial",
    isRecommended: true,
    bullets: [
      "Best balance of quality and speed",
      "Excellent for character likeness",
      "Community-proven results",
    ],
  },
  {
    id: "flux-1.1-pro",
    name: "FLUX 1.1 Pro",
    vendor: "Black Forest Labs",
    fidelity: 5,
    speed: 2,
    vram: 3,
    license: "API only, Pay per use",
    bullets: [
      "Highest fidelity output",
      "Best for production quality",
      "Requires API credits",
    ],
  },
  {
    id: "sdxl-1.0",
    name: "SDXL 1.0",
    vendor: "Stability AI",
    fidelity: 2,
    speed: 5,
    vram: 2,
    license: "Open, Commercial OK",
    bullets: [
      "Fastest training times",
      "Low VRAM requirements",
      "Full commercial rights",
    ],
  },
  {
    id: "sd3",
    name: "Stable Diffusion 3",
    vendor: "Stability AI",
    fidelity: 3,
    speed: 3,
    vram: 4,
    license: "Open",
    bullets: [
      "Latest architecture",
      "Good text rendering",
      "Balanced performance",
    ],
  },
];

// Default settings
export const DEFAULT_SETTINGS: TrainingSettings = {
  rank: 16,
  epochs: 32,
  learningRate: 0.0004,
  snapshotEvery: 8,
  augmentation: {
    horizontalFlip: true,
    cropJitter: true,
    colourJitter: false,
    noise: false,
  },
};

// Slide configuration
export const SLIDE_CONFIGS: SlideConfig[] = [
  {
    id: 0,
    name: "Model Selection",
    canAdvance: (state) => state.selectedModel !== null,
    canGoBack: () => false,
  },
  {
    id: 1,
    name: "References",
    canAdvance: (state) => state.references.length >= 5,
    canGoBack: (state) => !state.isLocked,
  },
  {
    // Real-mode-only step (mock mode skips it, like real mode skips Testing).
    // The real gate (prep run prepared) is applied in LoraWizard's slideConfigs.
    id: 2,
    name: "Prepare",
    canAdvance: () => true,
    canGoBack: (state) => !state.isLocked,
  },
  {
    id: 3,
    name: "Settings",
    canAdvance: () => true, // Has defaults
    canGoBack: (state) => !state.isLocked,
  },
  {
    id: 4,
    name: "Confirm",
    canAdvance: () => true, // Requires button click
    canGoBack: (state) => !state.isLocked,
  },
  {
    id: 5,
    name: "Training",
    canAdvance: (state) => !state.isTraining && state.trainingProgress !== null,
    canGoBack: () => false, // Locked during training
  },
  {
    id: 6,
    name: "Testing",
    canAdvance: (state) =>
      state.testResults.length === 4 &&
      state.testResults.every((t) => t.accepted),
    canGoBack: () => false, // Cannot go back to training
  },
  {
    id: 7,
    name: "Lock to Character",
    canAdvance: () => false, // Final slide
    canGoBack: () => false, // Cannot go back to testing
  },
];
